
import { useCallback } from 'react';
import { useProviderData } from './useProviderData';
import { useProviderDelete } from './useProviderDelete';
import { useProviderBulkDelete } from './useProviderBulkDelete';
import type { PEOProvider } from '@/types/peo.types';

/**
 * Main hook for PEO provider management
 */
export const usePEOProviders = () => {
  const { 
    providers, 
    setLocalProviders, 
    isLoading, 
    refetch,
    isError,
    error,
    isDeletingProvider, 
    setIsDeletingProvider,
    debugInfo
  } = useProviderData();
  
  const { handleProviderDelete } = useProviderDelete(
    setIsDeletingProvider, 
    refetch
  );
  
  const { 
    handleBulkDelete,
    isBulkDeleting,
    selectedProviders,
    handleSelectProvider,
    handleSelectAllProviders,
    clearSelectedProviders,
    deleteProgress
  } = useProviderBulkDelete(
    providers, 
    handleProviderDelete
  );

  // Filter providers by name (case insensitive) or by a list of providers
  const filterProviders = useCallback((searchTermOrProviders: string | PEOProvider[]) => {
    if (typeof searchTermOrProviders === 'string') {
      const searchTerm = searchTermOrProviders;
      if (!searchTerm.trim()) {
        console.log('Empty search term, refreshing all providers');
        refetch();
        return;
      }
      
      console.log(`Filtering providers by: "${searchTerm}"`);
      const filtered = providers.filter(
        provider => provider.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      console.log(`Found ${filtered.length} matching providers`);
      setLocalProviders(filtered);
    } else {
      // Handle case where an array of providers is passed
      const filteredProviders = searchTermOrProviders;
      console.log(`Setting ${filteredProviders.length} filtered providers`);
      setLocalProviders(filteredProviders);
    }
  }, [providers, setLocalProviders, refetch]);

  // Diagnostic logging for errors
  if (isError) {
    console.error("Error in usePEOProviders:", error);
    
    if (error instanceof Error) {
      console.error("Error name:", error.name);
      console.error("Error message:", error.message);
      
      // Check for specific error types to provide more helpful messages
      if (error.message.includes("permission denied")) {
        console.error("This appears to be a permissions issue. Check Supabase RLS policies.");
      } else if (error.message.includes("infinite recursion")) {
        console.error("There was an infinite recursion in a database policy, but it should be fixed now.");
        console.error("If you're still seeing this error, you may need to refresh the page.");
        console.error("Debug Info:", debugInfo);
      }
    }
  }

  // Format error message for display, with special handling for common issues
  const getErrorMessage = () => {
    if (!error) return '';
    
    // Handle Error objects
    if (error instanceof Error) {
      // Special handling for known error types
      if (error.message.includes("infinite recursion")) {
        return "The infinite recursion issue in the database policy has been fixed. Please try refreshing the page.";
      } else if (error.message.includes("permission denied")) {
        return "Permission denied: You don't have access to view PEO providers. Please check your admin role in Supabase.";
      } else {
        return error.message;
      }
    }
    
    // Handle string errors
    if (typeof error === 'string') {
      return error;
    }
    
    // Default case
    return 'Unknown error occurred';
  };

  return {
    providers,
    isLoading,
    isError,
    error: getErrorMessage(),
    refetch,
    filterProviders,
    handleProviderDelete,
    isDeletingProvider,
    handleBulkDelete,
    isBulkDeleting,
    selectedProviders,
    handleSelectProvider,
    handleSelectAllProviders,
    clearSelectedProviders,
    deleteProgress,
    debugInfo
  };
};
